import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useClientStore } from '@core/client/useClientStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import {
  Address,
  ClassificationGroup,
  ClientInput,
  ChargeType,
  LatLng,
} from '@core/generated/gql-types';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { useTenantId } from '@/contexts/TenantContext';
import {
  clientChargeTypeOptions,
  formatChargeTypeLabel,
  isMarginChargeType,
} from '@/utils/client.constants';
import AddressForm from '../forms/AddressForm';
import MultiSelect from '../shared/MultiSelect';
import SingleSelect from '../shared/SingleSelect';
import Button from '../Button';

type Props = {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Client name is required')
    .max(100, 'Name must be at most 100 characters'),
  abn: Yup.string()
    .test('abn', 'ABN must be 11 digits', (value) => {
      if (!value) return false;
      value = value.replaceAll(' ', '');
      return /^\d{11}$/.test(value);
    })
    .required('An Australian Business Number is required'),
  marginOverridePercentage: Yup.number()
    .min(0, 'Margin override percentage must be between 0 and 99')
    .max(99, 'Margin override percentage must be between 0 and 99')
    .nullable(),
  paymentTerm: Yup.number()
    .min(1, 'Payment term must be at least 1 day')
    .max(30, 'Payment term must be 30 days or less'),
  classifications: Yup.array().min(1, 'At least one industry is required'),
  address: Yup.object()
    .nullable()
    .test('address-required', 'Address is required', (value) => {
      return value !== null && value !== undefined;
    })
    .shape({
      addressLine1: Yup.string().required('Address is required'),
      suburb: Yup.string().required('Suburb is required'),
      state: Yup.string().required('State is required'),
      postcode: Yup.string().required('Postcode is required'),
      countryLabel: Yup.string().required('Country is required'),
    }),
});

const ClientCreateModal = ({ isModalOpen, setIsModalOpen }: Props) => {
  // Margin options are 2 to 98 in steps of 2. 100% would leave no charge to
  // mark up to.
  const marginOverridePercentageOptions = Array.from(
    { length: 49 },
    (_, i) => i * 2 + 2,
  );

  const { getTerm } = useTemplateStore();

  const [customMargin, setCustomMargin] = useState<boolean | undefined>(false);
  const [isManualAddressEntry, setIsManualAddressEntry] = useState(false);
  const [createSite, setCreateSite] = useState(true);

  const { createClient } = useClientStore();

  const {
    classificationGroups,
    fetchClassificationGroups,
    isLoading: isLoadingClassifications,
  } = useClassificationStore();

  const tenantId = useTenantId();

  const { coreLoopConfiguration, fetchAndSetCoreLoopConfiguration } =
    useCoreLoopConfigurationStore();

  const marginOverridePercentageEnabled =
    coreLoopConfiguration?.configuration?.allowClientMarginOverride;

  const marginPercentage = useMemo(() => {
    return coreLoopConfiguration?.configuration?.marginPercentage
      ? Math.round(coreLoopConfiguration?.configuration?.marginPercentage * 100)
      : undefined;
  }, [coreLoopConfiguration?.configuration?.marginPercentage]);

  useEffect(() => {
    if (isModalOpen && tenantId) {
      fetchClassificationGroups(tenantId);
      fetchAndSetCoreLoopConfiguration(tenantId);
    }
  }, [
    isModalOpen,
    tenantId,
    fetchClassificationGroups,
    fetchAndSetCoreLoopConfiguration,
  ]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      abn: '',
      paymentTerm: 7,
      marginOverridePercentage: marginPercentage,
      chargeType: ChargeType.Margin,
      classifications: [] as ClassificationGroup[],
      address: {
        addressLine1: '',
        addressLine2: '',
        suburb: '',
        state: '',
        postcode: '',
        countryLabel: '',
        countryValue: '',
      } as Address,
      latLng: null as LatLng | null,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus, resetForm }) => {
      setSubmitting(true);
      setStatus(undefined);

      try {
        if (!tenantId) {
          setStatus('User must be associated with a tenant');
          setSubmitting(false);
          return;
        }
        const payload: ClientInput = {
          name: values.name,
          tenantId: tenantId || '',
          // The API stores margin as a decimal 0-1 (e.g. 0.06 for 6%).
          // The form works in whole-percent (0-100), so convert before sending.
          marginOverridePercentage:
            values.marginOverridePercentage != null
              ? values.marginOverridePercentage / 100
              : values.marginOverridePercentage,
          paymentTerm: values.paymentTerm,
          chargeType: values.chargeType,
          address: values.address,
          latLng: values.latLng
            ? { lat: values.latLng.lat || 0, lng: values.latLng.lng || 0 }
            : undefined,
          createSite,
          customAttributes: [
            { ref: 'abn', value: values.abn.replaceAll(' ', '') },
          ],
          classifications: values.classifications.flatMap(
            (group) =>
              group.classifications?.map((c) => c?.id).filter(Boolean) || [],
          ) as string[],
        };
        const result = await createClient(payload);

        if (result.success && result.client) {
          resetForm();
          setIsModalOpen(false);
        } else {
          setStatus(
            result.error ||
              `Failed to create ${getTerm(TemplateTerm.CLIENT, false)}`,
          );
        }
      } catch (error) {
        setStatus(
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <>
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-h-[90vh] w-[600px] overflow-y-auto rounded-lg bg-white p-8 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-800">
                Create New {getTerm(TemplateTerm.CLIENT, false)}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {formik.status && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-600">{formik.status}</p>
              </div>
            )}

            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-8">
                <div className="col-span-2">
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="name"
                  >
                    {getTerm(TemplateTerm.CLIENT, false)} Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full rounded-md border bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500 ${
                      formik.touched.name && formik.errors.name
                        ? 'border-red-300'
                        : 'border-gray-300'
                    }`}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="mt-1 text-sm text-red-600">
                      {formik.errors.name}
                    </div>
                  )}
                </div>

                <div className="col-span-2">
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="abn"
                  >
                    ABN *
                  </label>
                  <input
                    type="text"
                    id="abn"
                    name="abn"
                    value={formik.values.abn}
                    onChange={formik.handleChange}
                    onKeyDown={(e) => {
                      // Block any input that is not a digit, space, backspace, or delete
                      if (
                        !/\d/.test(e.key) &&
                        e.key !== 'Backspace' &&
                        e.key !== 'Delete' &&
                        e.key !== ' '
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onBlur={formik.handleBlur}
                    className={`w-full rounded-md border bg-white px-4 py-2 focus:border-blue-500 focus:ring-blue-500 ${
                      formik.touched.abn && formik.errors.abn
                        ? 'border-red-300'
                        : 'border-gray-300'
                    }`}
                  />
                  {formik.touched.abn && formik.errors.abn && (
                    <div className="mt-1 text-sm text-red-600">
                      {formik.errors.abn}
                    </div>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Industries *
                  </label>
                  <MultiSelect
                    options={classificationGroups}
                    value={formik.values.classifications}
                    onChange={(value) =>
                      formik.setFieldValue('classifications', value)
                    }
                    placeholder="Select industries"
                    disabled={isLoadingClassifications}
                    isLoading={isLoadingClassifications}
                    loadingText="Loading industries..."
                    emptyText="No industries available"
                    error={
                      !!(
                        formik.touched.classifications &&
                        formik.errors.classifications
                      )
                    }
                  />
                  {formik.touched.classifications &&
                    formik.errors.classifications && (
                      <div className="mt-1 text-sm text-red-600">
                        {typeof formik.errors.classifications === 'string'
                          ? formik.errors.classifications
                          : 'At least one industry is required'}
                      </div>
                    )}
                </div>

                <div className="col-span-2">
                  <label
                    className="mb-1 block text-sm font-medium text-gray-700"
                    htmlFor="paymentTerm"
                  >
                    Payment Term
                  </label>

                  <SingleSelect
                    options={[7, 14, 30]}
                    value={formik.values.paymentTerm}
                    onChange={(value) =>
                      formik.setFieldValue('paymentTerm', value)
                    }
                    optionLabel={(option) => `${option} days`}
                  />
                </div>

                <div className="col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Charge Type
                  </label>
                  <SingleSelect<ChargeType>
                    options={clientChargeTypeOptions}
                    value={formik.values.chargeType}
                    onChange={(value) =>
                      formik.setFieldValue('chargeType', value)
                    }
                    optionLabel={(option) => formatChargeTypeLabel(option)}
                  />
                </div>

                {marginOverridePercentageEnabled &&
                  isMarginChargeType(formik.values.chargeType) && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Margin Override Percentage (%)
                      </label>
                      <p className="text-xs text-gray-500">
                        Enter a whole number between 0 and 99.
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCustomMargin(!customMargin)}
                          className={`my-2 rounded-md px-4 py-2 text-sm font-medium ${!customMargin ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} `}
                        >
                          Select Margin
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomMargin(!customMargin)}
                          className={`my-2 rounded-md px-4 py-2 text-sm font-medium ${customMargin ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} `}
                        >
                          Custom Margin
                        </button>
                      </div>
                      {customMargin ? (
                        <input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={99}
                          step={1}
                          id="marginOverridePercentage"
                          name="marginOverridePercentage"
                          value={formik.values.marginOverridePercentage ?? ''}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            formik.setFieldValue(
                              'marginOverridePercentage',
                              rawValue === '' ? undefined : Number(rawValue),
                            );
                          }}
                          onKeyDown={(e) => {
                            const allowedControlKeys = [
                              'Backspace',
                              'Delete',
                              'ArrowLeft',
                              'ArrowRight',
                              'ArrowUp',
                              'ArrowDown',
                              'Tab',
                              'Home',
                              'End',
                            ];
                            if (
                              !/^\d$/.test(e.key) &&
                              !allowedControlKeys.includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                          className={`w-full rounded-md border bg-white px-3 py-2 pr-8 focus:border-blue-500 focus:ring-blue-500 ${
                            formik.touched.marginOverridePercentage &&
                            formik.errors.marginOverridePercentage
                              ? 'border-red-300'
                              : 'border-gray-300'
                          }`}
                        />
                      ) : (
                        <SingleSelect
                          options={marginOverridePercentageOptions}
                          value={
                            formik.values.marginOverridePercentage ?? undefined
                          }
                          onChange={(value) =>
                            formik.setFieldValue(
                              'marginOverridePercentage',
                              value,
                            )
                          }
                        />
                      )}
                      {formik.touched.marginOverridePercentage &&
                        formik.errors.marginOverridePercentage && (
                          <div className="mt-1 text-sm text-red-600">
                            {formik.errors.marginOverridePercentage}
                          </div>
                        )}
                    </div>
                  )}

                <div className="col-span-2">
                  <AddressForm
                    onAddressChange={(address) =>
                      formik.setFieldValue('address', address)
                    }
                    onCoordinatesChange={(coordinates) =>
                      formik.setFieldValue('latLng', coordinates)
                    }
                    initialAddress={formik.values.address}
                    onManualEntryChange={setIsManualAddressEntry}
                  />
                  {/* When not in manual entry mode, only show "Address is required" */}
                  {!isManualAddressEntry && (
                    <>
                      {/* Show top-level address error if address is null */}
                      {formik.touched.address &&
                        formik.errors.address &&
                        typeof formik.errors.address === 'string' && (
                          <div className="mt-1 text-sm text-red-600">
                            {formik.errors.address}
                          </div>
                        )}
                      {/* Show addressLine1 error if address exists but field is invalid */}
                      {formik.touched.address &&
                        formik.errors.address &&
                        typeof formik.errors.address === 'object' &&
                        formik.errors.address?.addressLine1 && (
                          <div className="mt-1 text-sm text-red-600">
                            {formik.errors.address.addressLine1}
                          </div>
                        )}
                    </>
                  )}
                  {/* When in manual entry mode, show all field errors */}
                  {isManualAddressEntry && (
                    <>
                      {formik.touched.address?.addressLine1 &&
                        formik.errors.address?.addressLine1 && (
                          <div className="mt-1 text-sm text-red-600">
                            {formik.errors.address.addressLine1}
                          </div>
                        )}
                      {formik.touched.address?.suburb &&
                        formik.errors.address?.suburb && (
                          <div className="mt-1 text-sm text-red-600">
                            {formik.errors.address.suburb}
                          </div>
                        )}
                      {formik.touched.address?.state &&
                        formik.errors.address?.state && (
                          <div className="mt-1 text-sm text-red-600">
                            {formik.errors.address.state}
                          </div>
                        )}
                      {formik.touched.address?.postcode &&
                        formik.errors.address?.postcode && (
                          <div className="mt-1 text-sm text-red-600">
                            {formik.errors.address.postcode}
                          </div>
                        )}
                      {formik.touched.address?.countryLabel &&
                        formik.errors.address?.countryLabel && (
                          <div className="mt-1 text-sm text-red-600">
                            {formik.errors.address.countryLabel}
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setCreateSite((prev) => !prev)}
                  aria-pressed={createSite}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden ${
                    createSite ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      createSite ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium text-gray-700">
                  Create a Site at this address
                </span>
              </div>

              <div className="flex justify-end space-x-4 border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={formik.isSubmitting}
                  className="px-4 py-2 text-sm font-medium"
                >
                  {formik.isSubmitting
                    ? 'Creating...'
                    : `Create ${getTerm(TemplateTerm.CLIENT, false)}`}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientCreateModal;
